import { useEffect, useState } from 'react'

import type { Cost } from '@/shared/contract'

const reduced = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

/** Counts from 0 to `to` over ~600 ms (skips straight there under reduced motion). */
function useCountUp(to: number) {
  const [n, setN] = useState(0)
  useEffect(() => {
    if (reduced()) return setN(to)
    const start = performance.now()
    let raf = 0
    const tick = (t: number) => {
      const k = Math.min(1, (t - start) / 600)
      setN(Math.round(to * (1 - (1 - k) ** 3)))
      if (k < 1) raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [to])
  return n
}

export function CostPanel({ cost, sample }: { cost?: Cost; sample: boolean }) {
  const credits = useCountUp(cost?.credits ?? 0)
  return (
    <section aria-label="Cost" className="flex flex-col gap-2 rounded-lg border border-border p-3">
      <div className="flex items-baseline justify-between">
        <h2 className="font-heading text-sm font-semibold">
          {sample ? 'This run would cost' : 'This run'}
        </h2>
        <span className="font-mono text-xs text-muted-foreground">
          ${(cost?.usd ?? 0).toFixed(3)}
        </span>
      </div>
      <div className="font-mono text-3xl tabular-nums" aria-live="polite">
        {credits.toLocaleString()} <span className="text-base text-muted-foreground">credits</span>
      </div>
      {cost?.lines.length ? (
        <ul className="flex flex-col gap-0.5 text-xs text-muted-foreground">
          {cost.lines.map((l) => (
            <li key={l.event} className="flex justify-between gap-2 font-mono">
              <span>
                {l.units} × {l.event.replace(/_/g, ' ')} @ {l.rate}
              </span>
              <span className="tabular-nums">{l.credits}</span>
            </li>
          ))}
        </ul>
      ) : null}
      <p className="text-[11px] text-muted-foreground">
        Estimate at Pro rates. A Free account includes 15,000 credits/month.
      </p>
    </section>
  )
}
